// WHAT: Dialog component for collecting time tracking completion data and notes
// WHY:  Provides form interface for users to add notes, efficiency ratings, and reflection data when completing time tracking
// DOMAIN: Productivity → TimeTracQ → Notes Dialog
// DEPENDENCIES: Headless UI Dialog, Button component
// RISKS:
//   - NOTE: Form data is reset on close/confirm - unsaved changes are lost
//   - NOTE: Task selection is not yet implemented - taskId always null
//   - NOTE: Efficiency rating defaults to 5, may not reflect actual efficiency

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { type FC, useState } from "react";

import { Button } from "@/components/ui";

/**
 * WHAT:
 *   Interface defining data structure for time tracking completion information.
 *
 * WHY:
 *   Type-safe definition for completion data passed to parent component.
 */
export interface TrackingCompletionData {
  efficiencyRating?: null | number;
  impactScore?: null | string;
  notes?: null | string;
  taskId?: null | string;
  whatWasInefficient?: null | string;
  whatWentWell?: null | string;
}

/**
 * WHAT:
 *   Interface defining props for TrackingCompletionDialog component.
 *
 * WHY:
 *   Type-safe definition for component props and callbacks.
 */
interface TrackingCompletionDialogProperties {
  onClose: () => void;
  onConfirm: (data: TrackingCompletionData) => void;
  open: boolean;
}

/**
 * WHAT:
 *   React dialog component for collecting time tracking completion data and notes.
 *
 * WHY:
 *   Enables users to add reflection data, efficiency ratings, and notes when completing
 *   time tracking sessions for better productivity insights.
 *
 * BEHAVIOR:
 *   - Displays form with notes, efficiency rating, reflection fields, and impact score
 *   - Resets form state on close or confirm
 *   - Validates and collects all form data before confirmation
 *   - Uses Headless UI Dialog for accessibility
 *
 * RISKS:
 *   - WARN: Form resets on close - unsaved changes are lost
 *   - NOTE: Task selection is not implemented (taskId always null)
 *   - WARN: Efficiency rating defaults to 5, may not reflect actual efficiency
 *   - NOTE: No validation of field lengths or content
 *
 * @param root0
 *
 * @param root0.open
 *
 * @param root0.onClose
 *
 * @param root0.onConfirm
 *
 * @returns {JSX.Element} The tracking completion dialog component
 *
 */
const TrackingCompletionDialog: FC<TrackingCompletionDialogProperties> = ({
  onClose,
  onConfirm,
  open,
}) => {
  const [notes, setNotes] = useState("");
  const [efficiencyRating, setEfficiencyRating] = useState<null | number>(5);
  const [whatWentWell, setWhatWentWell] = useState("");
  const [whatWasInefficient, setWhatWasInefficient] = useState("");
  const [impactScore, setImpactScore] = useState<string>("");

  /**
   * WHAT:
   *   Handles form confirmation and passes data to parent component.
   *
   * WHY:
   *   Collects all form data, converts empty strings to null, and triggers callback.
   *
   * SIDE EFFECTS:
   *   - Calls onConfirm callback with collected data
   *   - Resets all form fields to initial state
   *
   * RISKS:
   *   - WARN: Task selection not implemented - taskId always null
   *   - NOTE: Empty strings are converted to null for consistency
   *   - WARN: No validation before confirmation
   */
  const handleConfirm = () => {
    onConfirm({
      efficiencyRating,
      impactScore: impactScore || null,
      notes: notes || null,
      // NOTE: Task selection is not yet implemented - taskId always null
      taskId: null,
      whatWasInefficient: whatWasInefficient || null,
      whatWentWell: whatWentWell || null,
    });
    // NOTE: Reset form after confirmation
    setNotes("");
    setEfficiencyRating(5);
    setWhatWentWell("");
    setWhatWasInefficient("");
    setImpactScore("");
  };

  /**
   * WHAT:
   *   Handles dialog close and resets form state.
   *
   * WHY:
   *   Ensures form is clean when dialog is reopened and prevents data leakage.
   *
   * SIDE EFFECTS:
   *   - Resets all form fields to initial state
   *   - Calls onClose callback to close dialog
   *
   * RISKS:
   *   - WARN: Unsaved changes are lost when dialog is closed
   *   - NOTE: Form reset happens before onClose callback
   */
  const handleClose = () => {
    // NOTE: Reset form on close to prevent data leakage
    setNotes("");
    setEfficiencyRating(5);
    setWhatWentWell("");
    setWhatWasInefficient("");
    setImpactScore("");
    onClose();
  };

  return (
    <Dialog className="relative z-50" onClose={handleClose} open={open} static>
      <div aria-hidden="true" className="fixed inset-0 bg-black bg-opacity-50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-[#1E293B] text-[#F1F5F9] rounded-2xl p-6 shadow-lg space-y-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="text-2xl font-semibold text-tracq-bright-blue">
            Tracking abschließen
          </DialogTitle>

          <div className="space-y-6">
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Notizen</label>
              <textarea
                className="w-full h-24 bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                placeholder="Was hast du gemacht?"
                value={notes}
              />
            </div>

            {/* Efficiency Rating */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Wie effizient war die Stunde? (1-10)
              </label>
              <div className="flex items-center gap-4">
                <input
                  className="flex-1 h-2 bg-[#212B36] rounded-lg appearance-none cursor-pointer accent-tracq-bright-blue"
                  max="10"
                  min="1"
                  onChange={(e) => {
                    setEfficiencyRating(Number.parseInt(e.target.value));
                  }}
                  type="range"
                  value={efficiencyRating ?? 5}
                />
                <div className="w-12 text-center text-lg font-semibold text-tracq-bright-blue">
                  {efficiencyRating ?? 5}
                </div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>1 - Sehr ineffizient</span>
                <span>10 - Sehr effizient</span>
              </div>
            </div>

            {/* What went well */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Was lief gut?</label>
              <textarea
                className="w-full h-32 bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  setWhatWentWell(e.target.value);
                }}
                placeholder="Beschreibe, was während dieser Stunde gut gelaufen ist..."
                value={whatWentWell}
              />
            </div>

            {/* What was inefficient */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Was war ineffizient?
              </label>
              <textarea
                className="w-full h-32 bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  setWhatWasInefficient(e.target.value);
                }}
                placeholder="Beschreibe, was während dieser Stunde ineffizient war..."
                value={whatWasInefficient}
              />
            </div>

            {/* Impact Score */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Impact Score</label>
              <select
                className="w-full bg-[#212B36] text-[#F1F5F9] border border-[#334155] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue"
                onChange={(e) => {
                  setImpactScore(e.target.value);
                }}
                value={impactScore}
              >
                <option value="">Keine Auswahl</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">Wie hoch ist der Impact dieser Arbeit?</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#334155]">
            <Button onClick={handleClose} variant="secondary">
              Abbrechen
            </Button>
            <Button onClick={handleConfirm} variant="primary">
              Speichern
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export { TrackingCompletionDialog };
